import React from "react";
import {
    Accordion, Avatar, Button, ButtonGroup, Chip, Div, FormItem, Input,
    ModalPage, ModalPageHeader, ModalRoot, PanelHeaderButton, RichCell, Spinner, Title
} from "@vkontakte/vkui";
import {Icon20AddCircleFillGreen, Icon20EditCircleFillBlue, Icon24BrowserBack, Icon28PicturePlusOutline, Icon16Clear} from "@vkontakte/icons";
import BaseComponent from "../../components/BaseComponent";
import TakeAPhoto from "../../components/TakeAPhoto";
import withOffline from "../../components/withOffline";
import * as constants from "../../assets/constants";
import StandKitsApi from "../../api/references/standkits";
import StandKitsManagers from "../../api/references/managers/standkitsManagers";
import StandCollectionsManagers from "../../api/references/managers/standcollectionsManagers";
import KitCollectionPicker from "./KitCollectionPicker";

const MODAL_KIT_FORM = 'stand_kit_form'
const KIT_PHOTO_EVENT = 'stand_kit_photo'

const today = () => new Date().toISOString().slice(0, 10)

const formatCreated = (ts) => {
    const n = Number(ts)
    if (!n) return 'не указано'
    const d = new Date(n * 1000)
    const p = (v) => String(v).padStart(2, '0')
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

const emptyForm = (standId) => ({
    id: 0,
    stand_id: standId,
    title: '',
    serial_number: '',
    date_from: today(),
    collection_ids: [],
    images: [],
})

/**
 * Комплектации стенда (ТЗ 241). Рендерится в карточке стенда под формой.
 * props: stand (объект стенда с id), current_user, isOffline (из withOffline)
 */
class StandKits extends BaseComponent {

    constructor(props) {
        super(props)
        this.state = {
            ...this.state,
            kits: [],
            collections: [],
            loading: false,
            form: null,
            numImage: 0,
            pickerTarget: null,
        }
    }

    componentDidMount() {
        super.componentDidMount()
        this.load()
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.stand ? prevProps.stand.id : null
        const nextId = this.props.stand ? this.props.stand.id : null
        if (prevId !== nextId) {
            this.load()
        }
    }

    load = async () => {
        const {stand, isOffline} = this.props
        if (!stand || !(stand.id > 0)) return
        this.setState({loading: true})
        const params = {stand_id: stand.id, show_deleted: false, isOffline, page: 1, epp: 500, search: ''}
        const [kitsRes, collRes] = await Promise.all([
            StandKitsManagers.getList(params),
            StandCollectionsManagers.getList(params),
        ])
        this.setState({
            kits: kitsRes.data.results || [],
            collections: collRes.data.results || [],
            loading: false,
        })
    }

    rememberCollection = (collection) => {
        const id = Number(collection.id || collection.uid)
        if (!id) return
        const title = collection.title || collection.name || ('#' + id)
        this.setState(state => ({
            collections: state.collections.some(item => Number(item.id) === id)
                ? state.collections
                : [...state.collections, {id, title}],
        }))
    }

    selectedCollectionIds = () => {
        if (this.state.pickerTarget === 'form') {
            return (this.state.form ? this.state.form.collection_ids : []).map(Number)
        }
        const kit = this.state.kits.find(item => Number(item.id) === Number(this.state.pickerTarget))
        return ((kit && kit.collection_ids) || []).map(Number)
    }

    excludedCollectionIds = () => {
        const selected = new Set(this.selectedCollectionIds())
        const ids = new Set()
        this.state.kits.forEach(kit => {
            if (this.state.pickerTarget !== 'form' && Number(kit.id) === Number(this.state.pickerTarget)) {
                return
            }
            ;(kit.collection_ids || []).forEach(id => {
                if (!selected.has(Number(id))) ids.add(Number(id))
            })
        })
        return [...ids]
    }

    openPicker = (target) => {
        if (this.props.isOffline) {
            this.openErrorNoty('В режиме offline нельзя менять комплектацию')
            return
        }
        this.setState({pickerTarget: target})
        this.setActiveModal(constants.MODAL_SELECT_LIST)
    }

    closePicker = () => {
        const back = this.state.pickerTarget === 'form' ? MODAL_KIT_FORM : null
        this.setState({pickerTarget: null})
        this.setActiveModal(back)
    }

    onConfirmCollections = async (loadedItems) => {
        const next = new Set(this.selectedCollectionIds())
        ;(loadedItems || []).forEach(collection => {
            const id = Number(collection.id || collection.uid)
            if (!id) return
            this.rememberCollection(collection)
            if (collection.checked) next.add(id)
            else next.delete(id)
        })
        const ids = [...next]
        if (this.state.pickerTarget === 'form') {
            this.setFormField('collection_ids', ids)
            this.setState({pickerTarget: null})
            this.setActiveModal(MODAL_KIT_FORM)
            return
        }
        const kit = this.state.kits.find(item => Number(item.id) === Number(this.state.pickerTarget))
        this.setState({pickerTarget: null})
        this.setActiveModal(null)
        if (!kit) return
        await this.saveComposition(kit, ids)
    }

    openForm = (kit = null) => {
        if (this.props.isOffline) {
            this.openErrorNoty('В режиме offline нельзя менять комплектацию')
            return
        }
        const form = kit
            ? {
                id: kit.id,
                stand_id: kit.stand_id,
                title: kit.title || '',
                serial_number: kit.serial_number || '',
                date_from: today(),
                collection_ids: [...(kit.collection_ids || [])].map(Number),
                images: Array.isArray(kit.images) ? kit.images.map(img => ({...img})) : [],
            }
            : emptyForm(this.props.stand.id)
        this.setState({form})
        this.setActiveModal(MODAL_KIT_FORM)
    }

    setFormField = (name, value) => {
        this.setState({form: {...this.state.form, [name]: value}})
    }

    getPhoto = (num) => {
        this.setState({numImage: num})
        this.setActiveModal(constants.MODAL_TAKE_A_PHOTO)
    }

    clearPhoto = (num) => {
        const images = [...this.state.form.images]
        images.splice(num, 1)
        this.setFormField('images', images)
    }

    receiveMessage = (event) => {
        const standId = this.props.stand ? this.props.stand.id : null
        if (event.data && event.data.type === 'standCollectionsChanged'
            && (!event.data.stand_id || String(event.data.stand_id) === String(standId))) {
            this.load()
        }
        if (event.data && event.data.type === KIT_PHOTO_EVENT && this.state.form) {
            const images = [...this.state.form.images]
            const old = images[event.data.data.numImage] ?? {}
            old.hasImage = true
            old.mainImage = event.data.data.image
            old.thumb = event.data.data.thumb
            images[event.data.data.numImage] = old
            this.setState({form: {...this.state.form, images}})
            this.setActiveModal(MODAL_KIT_FORM)
        }
    }

    saveComposition = async (kit, collectionIds) => {
        const previous = this.state.kits
        this.setState({
            kits: previous.map(item => Number(item.id) === Number(kit.id)
                ? {...item, collection_ids: collectionIds}
                : item),
        })
        try {
            const result = await StandKitsApi.save({
                id: kit.id,
                stand_id: kit.stand_id,
                title: kit.title,
                serial_number: kit.serial_number || '',
                deleted: kit.deleted || 0,
                date_from: today(),
                collection_ids: collectionIds,
                images: Array.isArray(kit.images) ? kit.images : [],
            })
            if (!result.data || !result.data.id) {
                throw new Error('empty')
            }
            await this.load()
        } catch (error) {
            this.setState({kits: previous})
            const text = error?.response?.data?.error || 'Не удалось сохранить комплектацию'
            this.openErrorNoty(text)
        }
    }

    removeFromKit = (kit, collectionId) => {
        if (this.props.isOffline) return
        const ids = (kit.collection_ids || []).map(Number).filter(id => id !== Number(collectionId))
        this.saveComposition(kit, ids)
    }

    removeFromForm = (collectionId) => {
        this.setFormField(
            'collection_ids',
            this.state.form.collection_ids.filter(id => Number(id) !== Number(collectionId))
        )
    }

    save = async () => {
        const {form} = this.state
        if (!form.title.trim()) {
            this.openErrorNoty('Укажите наименование комплектации')
            return
        }
        if (form.collection_ids.length === 0) {
            this.openErrorNoty('Отметьте хотя бы одну коллекцию')
            return
        }
        const result = await StandKitsApi.save(form)
        if (result.data && result.data.id) {
            window.postMessage({type: 'success', text: 'Комплектация сохранена', timeout: 1500, hideSpinner: true})
            this.setActiveModal(null)
            this.setState({form: null})
            this.load()
        } else {
            this.openErrorNoty('Не удалось сохранить комплектацию')
        }
    }

    delete = async (kit) => {
        if (this.props.isOffline) {
            this.openErrorNoty('В режиме offline нельзя менять комплектацию')
            return
        }
        await StandKitsApi.delete(kit.id)
        this.load()
    }

    collectionTitle = (id) => {
        const c = this.state.collections.find(el => String(el.id) === String(id))
        return c ? c.title : `#${id}`
    }

    renderPills = (ids, onRemove, onAdd) => {
        return <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '0 16px 8px'}}>
            {ids.map(id => <Chip key={id}
                                 className="stand-kit-pill"
                                 removable={!this.props.isOffline && !!onRemove}
                                 onRemove={() => onRemove(id)}>
                {this.collectionTitle(id)}
            </Chip>)}
            {!this.props.isOffline && onAdd &&
                <Button mode="outline" before={<Icon20AddCircleFillGreen/>}
                        style={{marginLeft: 'auto'}}
                        onClick={onAdd}>Добавить коллекции</Button>}
        </div>
    }

    renderKit = (kit) => {
        const ids = (kit.collection_ids || []).map(Number)
        return <div key={'kit' + kit.id}>
            <RichCell multiline
                      subhead={<div>
                          {kit.serial_number ? <span>Серийный номер: {kit.serial_number}. </span> : null}
                          {kit.date_from ? <span>Действует с {kit.date_from}. </span> : null}
                          <span>Создано: {formatCreated(kit.version_created || kit.created)}. </span>
                          <span>Автор: {kit.author_name || 'не указан'}</span>
                      </div>}
                      afterCaption={!this.props.isOffline && <div style={{display: 'flex', flexDirection: 'column'}}>
                          <Button mode="outline" size="m" onClick={() => this.openForm(kit)}><Icon20EditCircleFillBlue/></Button>
                          <Button mode="outline" size="m" onClick={() => this.delete(kit)}><Icon16Clear/></Button>
                      </div>}>
                <div className="stand-kit-head">
                    <span className="stand-kit-head__title">{kit.title}</span>
                    {ids.map(id => <Chip key={id} className="stand-kit-pill" removable={false}>
                        {this.collectionTitle(id)}
                    </Chip>)}
                </div>
            </RichCell>
            {this.renderArchive(kit)}
        </div>
    }

    renderForm() {
        const {form} = this.state
        if (!form) return null
        const base_url = constants.DEBUG ? constants.url_stage : constants.url_prod
        const images_url = base_url.replace("frontend/", '')

        return <div className="stand-kit-form stand-kit-form--fill">
            <div className="stand-kit-form__body">
                <FormItem top="Наименование скомплектованного стенда">
                    <Input value={form.title} onChange={e => this.setFormField('title', e.target.value)}/>
                </FormItem>
                <FormItem top="Серийный номер">
                    <Input value={form.serial_number} onChange={e => this.setFormField('serial_number', e.target.value)}/>
                </FormItem>
                <FormItem top="Дата начала действия" bottom="Если состав или фото меняются в другой день, предыдущая версия уйдёт в архив">
                    <Input type="date" value={form.date_from} onChange={e => this.setFormField('date_from', e.target.value)}/>
                </FormItem>
                <Div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Title level="2">Коллекции</Title>
                    {!this.props.isOffline &&
                        <Button mode="outline" before={<Icon20AddCircleFillGreen/>}
                                onClick={() => this.openPicker('form')}>Добавить коллекции</Button>}
                </Div>
                {this.renderPills(form.collection_ids.map(Number), this.removeFromForm)}
                <Div><Title level="3">Фото стенда</Title></Div>
                <Div className="incoming-photos-gallery" style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                    <Button mode="secondary" onClick={() => this.getPhoto(form.images.length)} style={{width: '100px'}}>
                        <Icon28PicturePlusOutline height={100}/>
                    </Button>
                    {form.images.map((img, index) => img && img.hasImage ? <div key={'img' + index} style={{position: 'relative'}}>
                        <Avatar mode="image" style={{borderRadius: '5%'}} size={100}
                                src={img.thumb || (images_url + String(img.mainImage || '').replace(images_url, ''))}/>
                        <Button mode="primary" size="s" style={{position: 'absolute', right: 0, bottom: 0}}
                                onClick={() => this.clearPhoto(index)} before={<Icon16Clear/>}/>
                    </div> : null)}
                </Div>
            </div>
            <div className="stand-kit-form__actions">
                <ButtonGroup mode="horizontal" stretched>
                    <Button stretched size="l" mode="secondary"
                            onClick={() => this.setActiveModal(null)}>Отмена</Button>
                    <Button stretched size="l" onClick={this.save}>Сохранить</Button>
                </ButtonGroup>
            </div>
        </div>
    }

    renderArchive(kit) {
        const versions = (kit.versions || []).filter(v => Number(v.archived) === 1)
        return <Accordion key={'arch' + kit.id}>
            <Accordion.Summary>Архив версий: {versions.length}</Accordion.Summary>
            {versions.length === 0 &&
                <RichCell multiline>Изменений состава ещё не было</RichCell>}
            {versions.map(v => <RichCell key={'v' + v.id} multiline
                                         subhead={`${v.date_from} — ${v.date_to || ''}. Создано: ${formatCreated(v.created)}. Автор: ${v.author_name || 'не указан'}`}>
                {(v.collection_ids || []).map(this.collectionTitle).join(', ') || 'Без коллекций'}
            </RichCell>)}
        </Accordion>
    }

    render() {
        const {stand} = this.props
        if (!stand || !(stand.id > 0)) return null
        const base_url = constants.DEBUG ? constants.url_stage : constants.url_prod

        const picking = this.state.pickerTarget && (
            this.state.activeModal === constants.MODAL_SELECT_LIST
            || this.state.activeModal === constants.MODAL_SELECT_LIST_ADD
        )
        const modals = picking
            ? <KitCollectionPicker
                activeModal={this.state.activeModal}
                setActiveModal={(modal) => this.setActiveModal(modal)}
                modalBack={this.closePicker}
                onConfirm={this.onConfirmCollections}
                selectedIds={this.selectedCollectionIds()}
                excludeIds={this.excludedCollectionIds()}
                existingTitles={this.state.collections.map(item => item.title)}
                isOffline={this.props.isOffline}
                modal_title="Коллекции"
            />
            : <ModalRoot activeModal={this.state.activeModal}>
                <ModalPage id={MODAL_KIT_FORM} onClose={() => this.setActiveModal(null)} settlingHeight={100}
                           header={<ModalPageHeader before={<PanelHeaderButton onClick={() => this.setActiveModal(null)}>
                               <Icon24BrowserBack/></PanelHeaderButton>}>
                               {this.state.form && this.state.form.id > 0 ? 'Комплектация' : 'Новая комплектация'}
                           </ModalPageHeader>}>
                    {this.renderForm()}
                </ModalPage>
                <ModalPage id={constants.MODAL_TAKE_A_PHOTO} onClose={() => this.setActiveModal(MODAL_KIT_FORM)}>
                    <TakeAPhoto eventName={KIT_PHOTO_EVENT}
                                destination="brands"
                                numImage={this.state.numImage}
                                hidePhotos={() => this.setActiveModal(MODAL_KIT_FORM)}
                                url={base_url}
                                openErrorNoty={this.openErrorNoty}/>
                </ModalPage>
            </ModalRoot>

        return <div className="stand-kits-list">
            <div className={this.state.activeModal ? "stand-kit-modal-host" : undefined}>
                {modals}
            </div>
            <Div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Title level="2">Комплектации</Title>
                <Button mode="outline" before={<Icon20AddCircleFillGreen/>} onClick={() => this.openForm()}>Добавить</Button>
            </Div>
            {this.state.loading && <Spinner size="medium"/>}
            {this.state.kits.map(kit => this.renderKit(kit))}
        </div>
    }
}

export default withOffline(StandKits)
